/**
 * Room state module.
 *
 * Handles reading room state.
 *
 * Notes:
 * - This module currently only exposes reads (`getRoomState`); writes are done by other modules.
 * - `getRoomState` returns public serialized state.
 * - Reconstructed game state stays in the client, outside room storage.
 */

import type { RoomStorage } from '../../types/infrastructure.types'
import type { PublicRoomState } from '../../types/room.types'
import { serializeRoomState } from '../publicState/publicState'

export interface RoomStateContainer {
  /** Canonical persistence for room state (mates + activeGame). */
  storage: RoomStorage
}

/**
 * Creates state management functions for a room.
 */
export function makeRoomState(container: RoomStateContainer) {
  const { storage } = container

  /**
   * Get the current room state.
   *
   * This is primarily used for:
   * - local mode initialization (pull full state after creating a local room)
   * - tests/debug tooling
   */
  const getRoomState = async (): Promise<PublicRoomState> => {
    return serializeRoomState(storage.getState())
  }

  return { getRoomState }
}
